/**
 * English UI strings — the source of truth for the key set.
 *
 * `ru.ts` is typed `Record<TKey, string>`, so a key added here and forgotten
 * there is a compile error rather than a silently untranslated label.
 *
 * Plural forms use a `#<category>` suffix resolved via Intl.PluralRules.
 */
export const en = {
  'app.name': 'MeowTracker',
  'app.tagline': 'Fallout 76 reward tracker',

  'meow.label': 'Meow',
  'meow.bubble': 'Meow!',
  'meow.aria': 'Mark “{name}” as collected',
  'meow.all': 'Meow all',
  'meow.none': 'Unmeow all',

  'filter.search': 'Search rewards…',
  'filter.searchAria': 'Search rewards by name, source or note',
  'filter.all': 'All',
  'filter.done': 'Meowed',
  'filter.todo': 'Not yet',
  'filter.rarity': 'Rarity',
  'filter.type': 'Type',
  'filter.reset': 'Reset filters',
  'filter.empty': 'Nothing matches these filters.',
  'filter.emptyHint': 'Try clearing the search or switching back to All.',

  'stats.collected': '{done} / {total}',
  'stats.percent': '{percent}%',
  'stats.remaining#one': '{n} left',
  'stats.remaining#few': '{n} left',
  'stats.remaining#many': '{n} left',
  'stats.remaining#other': '{n} left',
  'stats.allDone': 'All done — nice work.',
  'stats.progressAria': '{done} of {total} rewards collected',

  'group.collapse': 'Collapse group',
  'group.expand': 'Expand group',
  'group.jump': 'Jump to group',

  'phase.live': 'Live',
  'phase.daysLeft#one': '{n} day left',
  'phase.daysLeft#few': '{n} days left',
  'phase.daysLeft#many': '{n} days left',
  'phase.daysLeft#other': '{n} days left',
  'phase.lastDay': 'Last day',
  'phase.startsIn#one': 'Starts in {n} day',
  'phase.startsIn#few': 'Starts in {n} days',
  'phase.startsIn#many': 'Starts in {n} days',
  'phase.startsIn#other': 'Starts in {n} days',
  'phase.startsTomorrow': 'Starts tomorrow',
  'phase.ended': 'Ended',

  'now.live': 'Live now',
  'now.next': 'Next',
  'now.before': 'The event starts on {date}',
  'now.after': 'The event has ended.',

  'odds.label': 'Odds',
  'odds.expected#one': '≈{value} attempt on average',
  'odds.expected#few': '≈{value} attempts on average',
  'odds.expected#many': '≈{value} attempts on average',
  'odds.expected#other': '≈{value} attempts on average',
  'odds.ninety': '90% chance within {value}',
  'odds.explain':
    'For the items still missing, assuming one loot roll per attempt at the listed chances. Guaranteed drops and unknown chances are left out.',

  'undo.meowed': 'Meowed {n}',
  'undo.unmeowed': 'Unmeowed {n}',
  'undo.action': 'Undo',

  'share.title': 'Share as picture',
  'share.modeAll': 'Everything',
  'share.modeDone': 'Collected only',
  'share.share': 'Share…',
  'share.copy': 'Copy image',
  'share.copied': 'Picture copied — paste it into chat.',
  'share.copyFailed': 'Could not copy the picture.',
  'share.download': 'Download PNG',
  'share.rendering': 'Rendering…',
  'share.failed': 'Could not render the picture.',
  'share.hint': 'On a phone, press and hold the picture to save it.',
  'share.imageAlt': 'Preview of the progress picture',
  'share.nothingYet': 'Nothing collected yet.',
  'share.variantList': 'List',
  'share.variantMap': 'Map',

  'view.list': 'List',
  'view.map': 'Map',

  'map.zoomIn': 'Zoom in',
  'map.zoomOut': 'Zoom out',
  'map.fit': 'Show the whole map',
  'map.loading': 'Loading the map…',
  'map.pinAria': '{name}: {done} of {total}',
  'map.placeAll': 'Meow all here',
  'map.placeNone': 'Unmeow all here',
  'map.placesCleared': '{done} / {total} places cleared',

  'profile.default': 'Main',
  'profile.label': 'Profile',
  'profile.new': 'New profile',
  'profile.rename': 'Rename',
  'profile.duplicate': 'Duplicate',
  'profile.delete': 'Delete',
  'profile.namePlaceholder': 'Character name',
  'profile.deleteConfirm': 'Delete “{name}” and all its progress? This cannot be undone.',
  'profile.deleteLast': 'You cannot delete your only profile.',
  'profile.copySuffix': '{name} (copy)',

  'settings.title': 'Settings',
  'settings.language': 'Language',
  'settings.showBothNames': 'Show English name under Russian',
  'settings.scanlines': 'Scanline overlay',
  'settings.data': 'Data',
  'settings.export': 'Export backup',
  'settings.import': 'Import backup',
  'settings.importMode': 'Import mode',
  'settings.importReplace': 'Replace everything',
  'settings.importMerge': 'Merge with existing',
  'settings.importDone': 'Backup imported.',
  'settings.importFailed': 'That file is not a MeowTracker backup.',
  'settings.resetSet': 'Reset this checklist',
  'settings.resetConfirm': 'Clear all progress for “{name}”?',
  'settings.prune': 'Remove progress for deleted items',
  'settings.storageNote': 'Progress is saved in this browser only. Export a backup to move it.',

  'item.untranslated': 'No Russian name found — showing the English one.',
  'item.reviewNeeded': 'Machine-matched translation, may need review.',

  'rarity.guaranteed': 'Guaranteed',
  'rarity.common': 'Common',
  'rarity.rare': 'Rare',
  'rarity.ultra-rare': 'Ultra rare',
  'rarity.legendary': 'Legendary',
  'rarity.unknown': 'Unknown',

  'type.plan': 'Plan',
  'type.weapon': 'Weapon',
  'type.armor': 'Armor',
  'type.apparel': 'Apparel',
  'type.cosmetic': 'Cosmetic',
  'type.consumable': 'Consumable',
  'type.mod': 'Mod',
  'type.title': 'Title',
  'type.journal': 'Journal',
  'type.misc': 'Misc',
  'type.unknown': 'Other',

  'footer.sources': 'Sources',
  'footer.ruesoTitle': 'Russian item names',

  'sync.title': 'Sync across devices',
  'sync.offNote':
    'Progress stays in this browser. Turn on sync to see the same checklist on your phone.',
  'sync.turnOn': 'Turn on sync',
  'sync.haveCode': 'I have a code',
  'sync.enterPrompt': 'Paste the code from your other device',
  'sync.join': 'Connect',
  'sync.codeLabel': 'Your sync code',
  'sync.secretNote':
    'Anyone with this code can read and change your checklist, and it is also the key — without it nobody, including this site, can read the cloud copy.',
  'sync.showQr': 'Show QR',
  'sync.hideQr': 'Hide QR',
  'sync.qrAlt': 'QR code that opens this tracker already paired',
  'sync.scanNote': 'Scan it with your phone camera — it opens the tracker already paired.',
  'sync.copyLink': 'Copy link',
  'sync.copyCode': 'Copy code',
  'sync.copied': 'Copied to the clipboard.',
  'sync.copyFailed': 'Could not copy — select the code and copy it by hand.',
  'sync.stop': 'Stop syncing here',
  'sync.stopErase': 'Stop and erase the cloud copy',
  'sync.stopConfirm': 'Stop syncing on this device? The progress here stays.',
  'sync.eraseConfirm':
    'Erase the cloud copy? Other devices stop receiving updates, but keep what they already have.',
  'sync.statusSyncing': 'Syncing…',
  'sync.statusIdle': 'Synced at {time}',
  'sync.statusWaiting': 'Waiting for the first sync…',
  'sync.fault.network': 'Offline — it will retry on its own.',
  'sync.fault.code': 'That code does not match the data stored under it.',
  'sync.fault.schema': 'The cloud copy comes from a newer version. Update this device first.',
  'sync.fault.server': 'The sync service is not answering.',
  'sync.badCode': 'That is not a valid sync code.',
  'sync.paired': 'Paired — your checklists are merged.',
  'sync.pairFailed': 'Could not pair with that code.',
  'sync.limits':
    'Ticks merge item by item, so two devices never overwrite each other. Deleting a profile is local — do that on each device.',

  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.close': 'Close',
  'common.source': 'Source',
  'common.chance': 'Chance',
} as const;

/** Keys exactly as they appear in the tables, including `#one` variants. */
export type TableKey = keyof typeof en;

/** "stats.remaining#one" -> "stats.remaining" */
type PluralBase<K extends string> = K extends `${infer Base}#${string}` ? Base : never;

/**
 * What callers may pass to `t()`: any literal key, plus the base of any plural
 * family. Passing a base key without an `n` param falls through to the raw key,
 * so the plural form is only reachable the intended way.
 */
export type TKey = TableKey | PluralBase<TableKey>;
